import React from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Box,
  CircularProgress,
  Typography,
} from '@mui/material';
import { ExpandMore, PhoneInTalk } from '@mui/icons-material';
import type { SxProps, Theme } from '@mui/material/styles';
import VoiceCallDetails from './VoiceCallDetails';

// PHA-1234: the full call transcript and recording, as a collapsed section. It used
// to be written out only on the Surveys tab; the Event Log offered nothing but a 16px
// phone icon inside the collapsed Responses list. One component so both tabs render
// the same thing. Details load on first expand, from the caller's cache.

interface VoiceCallTranscriptAccordionProps {
  voiceCallLogId: string;
  /** The GET /voice-calls/:id payload, once loaded. */
  details?: any;
  loading?: boolean;
  /** Called on expand; the caller fetches (and caches) the details. */
  onExpand: (voiceCallLogId: string) => void;
  sx?: SxProps<Theme>;
}

/** The voice_call_log_id behind a set of survey answers, if any were collected by voice. */
export const voiceCallLogIdOf = (
  answers?: { raw_data?: { source?: string; voice_call_log_id?: string } }[]
): string | undefined =>
  answers?.find((answer) => answer.raw_data?.source === 'voice_call' && answer.raw_data?.voice_call_log_id)
    ?.raw_data?.voice_call_log_id;

const VoiceCallTranscriptAccordion: React.FC<VoiceCallTranscriptAccordionProps> = ({
  voiceCallLogId,
  details,
  loading,
  onExpand,
  sx,
}) => (
  <Accordion
    sx={sx}
    data-testid="voice-call-transcript-accordion"
    onChange={(event, expanded) => {
      if (expanded) {
        onExpand(voiceCallLogId);
      }
    }}
  >
    <AccordionSummary expandIcon={<ExpandMore />}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PhoneInTalk sx={{ color: '#9c27b0' }} />
        <Typography variant="subtitle2">
          View Call Transcript & Recording
        </Typography>
      </Box>
    </AccordionSummary>
    <AccordionDetails>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : details ? (
        <VoiceCallDetails
          voiceCallLogId={voiceCallLogId}
          hasRecording={details.hasRecording}
          transcript={details.transcript}
          annotatedTranscript={details.annotatedTranscript}
          durationSeconds={details.durationSeconds}
          status={details.status}
          callId={details.callId}
        />
      ) : (
        <Alert severity="info">
          No voice call details available.
        </Alert>
      )}
    </AccordionDetails>
  </Accordion>
);

export default VoiceCallTranscriptAccordion;
